import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Eye, EyeOff, FileText, LayoutList, Send, Sparkles, Trash2 } from "lucide-react";

import { cn } from "@/libs/cn";
import { useSessionStore } from "@/modules/auth/store/useSessionStore";
import Button from "@/modules/common/components/files/Button";
import DeleteModal from "@/modules/common/components/files/DeleteModal";
import Input from "@/modules/common/components/files/Input";
import { Modal, ModalBody } from "@/modules/common/components/files/Modal";
import PageBreadcrumb from "@/modules/common/components/files/PageBreadcrumb";
import SelectV2 from "@/modules/common/components/files/SelectV2";
import Spinner from "@/modules/common/components/files/Spinner";
import Tag from "@/modules/common/components/files/Tag";
import useNavigateBack from "@/modules/common/hooks/useNavigateBack";
import useToastOnError from "@/modules/common/hooks/useToastOnError";
import { AdminUserRole } from "@/modules/common/types/Users.enum";
import { getApiErrorMessage } from "@/modules/common/utils/apiError";
import { formatDate } from "@/modules/common/utils/format";
import { ROUTES } from "@/routes/constants/routes";
import { ADMIN_RELEASE_NOTES_ENDPOINTS, AdminReleaseNotesService } from "@/service/api/admin-release-notes";
import { ReleaseNoteResponse } from "@/service/response/admin-release-notes";

import BlockEditor from "../../knowledge-base/components/BlockEditor";
import MarkdownPreview from "../../knowledge-base/components/MarkdownPreview";
import { blocksToMarkdown, ContentBlock, markdownToBlocks, previewUrlsByKey, withPreviewUrls } from "../../knowledge-base/markdown";
import { hasPendingUploads } from "../../knowledge-base/media/media-placeholder";
import { ReleaseNoteDraft } from "../chat-answer";
import GenerateReleaseNoteModal from "../components/GenerateReleaseNoteModal";
import {
  RELEASE_NOTE_AUDIENCE_OPTIONS,
  RELEASE_NOTE_MEDIA_PREFIX,
  ReleaseNoteStatus,
  releaseNoteStatusLabel,
  releaseNoteStatusVariant,
  uploadReleaseNoteMedia,
} from "../constants";

const DocumentEditor = lazy(() => import("../../knowledge-base/components/DocumentEditor"));

type EditorMode = "document" | "blocks";

const EDITOR_MODES = [
  { mode: "document" as const, label: "Documento", icon: FileText },
  { mode: "blocks" as const, label: "Blocos", icon: LayoutList },
];

const ReleaseNoteEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canUserSee } = useSessionStore();
  const canWrite = canUserSee(AdminUserRole.ADMIN);
  const navigateBack = useNavigateBack(ROUTES.RELEASE_NOTES.LIST);

  const detail = useQuery({
    queryKey: [ADMIN_RELEASE_NOTES_ENDPOINTS.LIST, id],
    queryFn: () => AdminReleaseNotesService.get(id!),
    enabled: Boolean(id),
  });

  useToastOnError(detail.isError, detail.error, "Não foi possível carregar esta nota.", navigateBack);

  const [title, setTitle] = useState("");
  const [audiences, setAudiences] = useState<string[]>([]);
  const [markdown, setMarkdown] = useState("");
  const [blocks, setBlocks] = useState<ContentBlock[]>([]);
  const [editorMode, setEditorMode] = useState<EditorMode>("document");
  const [previewByKey, setPreviewByKey] = useState<Map<string, string>>(() => new Map());
  const [status, setStatus] = useState(ReleaseNoteStatus.DRAFT);
  const [version, setVersion] = useState<string>();
  const [publishedAt, setPublishedAt] = useState<string | null>(null);
  const [hydratedId, setHydratedId] = useState<string>();
  const [editorRevision, setEditorRevision] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [pending, setPending] = useState<string>();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const mediaRefreshed = useRef(false);

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const applyNote = (note: ReleaseNoteResponse) => {
    setStatus(note.status);
    setVersion(note.version);
    setPublishedAt(note.publishedAt);
    setHydratedId(note.id);
  };

  if (detail.data && hydratedId !== detail.data.id) {
    setTitle(detail.data.title);
    setAudiences(detail.data.audiences.map(String));
    const previews = previewUrlsByKey(detail.data.content, detail.data.renderedContent ?? "", RELEASE_NOTE_MEDIA_PREFIX);
    setMarkdown(detail.data.content);
    setBlocks(markdownToBlocks(detail.data.content, previews, RELEASE_NOTE_MEDIA_PREFIX));
    setPreviewByKey(previews);
    applyNote(detail.data);
  }

  const locked = !canWrite;
  const currentMarkdown = () => (editorMode === "blocks" ? blocksToMarkdown(blocks) : markdown);
  const previewMarkdown =
    editorMode === "blocks"
      ? blocksToMarkdown(
          blocks.map((block) =>
            (block.kind === "image" || block.kind === "video") && block.previewUrl ? { ...block, key: block.previewUrl } : block,
          ),
        )
      : withPreviewUrls(markdown, previewByKey);

  const remember = (note: ReleaseNoteResponse) => {
    applyNote(note);
    setDirty(false);
    queryClient.setQueryData([ADMIN_RELEASE_NOTES_ENDPOINTS.LIST, note.id], note);
    queryClient.invalidateQueries({ queryKey: [ADMIN_RELEASE_NOTES_ENDPOINTS.LIST] });
  };

  const save = async (status?: ReleaseNoteStatus) => {
    if (!title.trim() || !audiences.length) {
      toast.error("Preencha o título e para quem a nota está disponível.");
      return null;
    }

    if (hasPendingUploads(currentMarkdown())) {
      toast.error("Aguarde o envio das mídias terminar.");
      return null;
    }

    const payload = { title: title.trim(), audiences: audiences.map(Number), content: currentMarkdown() };

    if (id) {
      const saved = await AdminReleaseNotesService.update(id, { ...payload, status });
      remember(saved);
      return saved;
    }

    const created = await AdminReleaseNotesService.create(payload);
    remember(created);
    if (!status || status === created.status) return created;

    try {
      const saved = await AdminReleaseNotesService.update(created.id, { status });
      remember(saved);
      return saved;
    } finally {
      openCreated(created);
    }
  };

  const openCreated = (note: ReleaseNoteResponse) => {
    if (id) return;
    navigate(generatePath(ROUTES.RELEASE_NOTES.EDIT, { id: note.id }), { replace: true });
  };

  const run = async (action: string, task: () => Promise<void>) => {
    setPending(action);
    try {
      await task();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Não foi possível concluir a ação."));
    } finally {
      setPending(undefined);
    }
  };

  const saveDraft = () =>
    run("draft", async () => {
      const saved = await save();
      if (!saved) return;
      toast.success(saved.status === ReleaseNoteStatus.PUBLISHED ? "Nota salva." : `Rascunho salvo — versão ${saved.version}.`);
      openCreated(saved);
    });

  const publish = () =>
    run("publish", async () => {
      if (!currentMarkdown().trim()) {
        toast.error("Escreva o conteúdo antes de publicar.");
        return;
      }
      const saved = await save(ReleaseNoteStatus.PUBLISHED);
      if (!saved) return;
      toast.success("Nota publicada.");
    });

  const unpublish = () =>
    run("unpublish", async () => {
      if (!id) return;
      const saved = await save(ReleaseNoteStatus.DRAFT);
      if (!saved) return;
      toast.success("Nota despublicada. Ela voltou para rascunho.");
    });

  const remove = () =>
    run("delete", async () => {
      if (!id) return;
      await AdminReleaseNotesService.remove(id);
      setDirty(false);
      await queryClient.invalidateQueries({ queryKey: [ADMIN_RELEASE_NOTES_ENDPOINTS.LIST] });
      toast.success("Nota excluída.");
      navigate(ROUTES.RELEASE_NOTES.LIST, { replace: true });
    });

  const applyGenerated = (draft: ReleaseNoteDraft) => {
    if (draft.title) setTitle(draft.title);
    setMarkdown(draft.content);
    setBlocks(markdownToBlocks(draft.content, previewByKey, RELEASE_NOTE_MEDIA_PREFIX));
    setEditorRevision((revision) => revision + 1);
    setDirty(true);
  };

  const refreshPreviewUrls = async () => {
    if (!id || mediaRefreshed.current) return;
    mediaRefreshed.current = true;

    const { data } = await detail.refetch();
    if (!data) return;

    const fresh = previewUrlsByKey(data.content, data.renderedContent ?? "", RELEASE_NOTE_MEDIA_PREFIX);
    fresh.forEach((url, key) => previewByKey.set(key, url));
    setPreviewByKey(new Map(previewByKey));
    setBlocks((current) =>
      current.map((block) => (block.key && fresh.has(block.key) ? { ...block, previewUrl: fresh.get(block.key)! } : block)),
    );
  };

  if (id && detail.isLoading) {
    return (
      <div className="flex min-h-80 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (id && detail.isError) return null;

  const switchMode = (mode: EditorMode) => {
    if (mode === editorMode) return;
    if (hasPendingUploads(currentMarkdown())) {
      toast.error("Aguarde o envio das mídias terminar antes de trocar o modo de edição.");
      return;
    }
    if (mode === "document") {
      blocks.forEach((block) => {
        if (block.key && block.previewUrl) previewByKey.set(block.key, block.previewUrl);
      });
      setMarkdown(blocksToMarkdown(blocks));
      setEditorRevision((revision) => revision + 1);
    } else {
      setBlocks(markdownToBlocks(markdown, previewByKey, RELEASE_NOTE_MEDIA_PREFIX));
    }
    setEditorMode(mode);
  };

  const heading = id ? (canWrite ? "Editar nota" : "Nota de atualização") : "Nova nota";

  return (
    <div className="flex flex-col gap-5">
      <PageBreadcrumb items={[{ label: "Notas de Atualização", to: ROUTES.RELEASE_NOTES.LIST }, { label: heading }]} />

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-[22px] font-bold text-ui-primary">{heading}</h1>
            <Tag label={releaseNoteStatusLabel(status)} variant={releaseNoteStatusVariant(status)} size="small" />
          </div>
          <dl className="mt-1 flex flex-wrap gap-x-6 gap-y-1 text-sm text-ui-text-muted">
            <div className="flex gap-1">
              <dt>Versão:</dt>
              <dd className="font-semibold text-ui-primary">{version ?? "será gerada automaticamente ao salvar"}</dd>
            </div>
            <div className="flex gap-1">
              <dt>Publicada em:</dt>
              <dd className="font-semibold text-ui-primary">{publishedAt ? formatDate(publishedAt, "DD/MM/YYYY") : "—"}</dd>
            </div>
          </dl>
        </div>
        <Button type="button" variant="outlined" color="primary" icon={<ArrowLeft size={16} />} label="Voltar" onClick={navigateBack} />
      </header>

      <section className="rounded-3xl border border-ui-border bg-white p-5 shadow-sm md:p-6">
        <Input
          label="Título da nota"
          markAsRequired
          disabled={locked}
          value={title}
          placeholder="Ex.: Novidades da plataforma"
          onChange={(event) => {
            setTitle(event.target.value);
            setDirty(true);
          }}
        />
        <div className="mt-4">
          <SelectV2
            label="Disponível para"
            markAsRequired
            disabled={locked}
            options={RELEASE_NOTE_AUDIENCE_OPTIONS}
            placeholder="Gestora, cliente ou ambos"
            value={audiences}
            onChange={(value) => {
              setAudiences(value);
              setDirty(true);
            }}
          />
        </div>
      </section>

      <section className="rounded-3xl border border-ui-border bg-white p-5 shadow-sm md:p-6">
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-ui-primary">Conteúdo</h2>
            <p className="mt-1 text-sm text-ui-text-muted">
              {editorMode === "document"
                ? "Escreva como num editor de texto. Para subtítulo, comece uma linha nova com ## e espaço (ou escolha Título 2 no menu Parágrafo). Para lista, comece com - e espaço."
                : "Monte a nota em blocos e reordene como preferir."}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <div role="tablist" aria-label="Modo de edição" className="inline-flex rounded-xl bg-[#EEF1F5] p-1">
              {EDITOR_MODES.map(({ mode, label, icon: Icon }) => (
                <button
                  key={mode}
                  type="button"
                  role="tab"
                  aria-selected={editorMode === mode}
                  onClick={() => switchMode(mode)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition cursor-pointer",
                    editorMode === mode ? "bg-white font-medium text-ui-primary shadow-sm" : "text-ui-text-muted hover:text-ui-primary",
                  )}
                >
                  <Icon size={15} />
                  {label}
                </button>
              ))}
            </div>
            {canWrite && (
              <Button
                type="button"
                variant="outlined"
                color="primary"
                size="small"
                icon={<Sparkles size={15} />}
                label="Gerar com IA"
                disabled={Boolean(pending)}
                onClick={() => setGenerateOpen(true)}
              />
            )}
          </div>
        </div>
        {editorMode === "blocks" ? (
          <BlockEditor
            blocks={blocks}
            disabled={locked}
            uploadFile={uploadReleaseNoteMedia}
            onChange={(next) => {
              setBlocks(next);
              setDirty(true);
            }}
          />
        ) : (
          <Suspense
            fallback={
              <div className="flex min-h-80 items-center justify-center">
                <Spinner />
              </div>
            }
          >
            <DocumentEditor
              key={`${hydratedId ?? "new"}-${editorRevision}`}
              markdown={markdown}
              disabled={locked}
              previewByKey={previewByKey}
              placeholder="Escreva o que mudou na plataforma…"
              contentLabel="Conteúdo da nota"
              uploadFile={uploadReleaseNoteMedia}
              fullWidth
              onChange={(value, initialNormalize) => {
                setMarkdown(value);
                if (!initialNormalize) setDirty(true);
              }}
            />
          </Suspense>
        )}
      </section>

      <footer className="sticky -bottom-6 z-20 -mx-6 -mb-6 flex flex-wrap items-center justify-between gap-3 border-t border-ui-border bg-white/95 px-6 py-3 backdrop-blur">
        <div className="flex flex-wrap items-center gap-2">
          <SaveState dirty={dirty} hasId={Boolean(id)} locked={locked} />
          {canWrite && status === ReleaseNoteStatus.PUBLISHED && (
            <Button
              type="button"
              variant="text"
              color="warning"
              size="small"
              icon={<EyeOff size={15} />}
              label="Despublicar"
              loading={pending === "unpublish"}
              disabled={Boolean(pending)}
              onClick={unpublish}
            />
          )}
          {canWrite && id && (
            <Button
              type="button"
              variant="text"
              color="danger"
              size="small"
              icon={<Trash2 size={15} />}
              label="Excluir"
              disabled={Boolean(pending)}
              onClick={() => setDeleteOpen(true)}
            />
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            variant="outlined"
            color="primary"
            icon={<Eye size={16} />}
            label="Visualizar"
            onClick={() => {
              mediaRefreshed.current = false;
              setPreviewOpen(true);
            }}
          />
          {canWrite && status === ReleaseNoteStatus.PUBLISHED && (
            <Button
              type="button"
              color="success"
              label="Salvar alterações"
              loading={pending === "draft"}
              disabled={Boolean(pending)}
              onClick={saveDraft}
            />
          )}
          {canWrite && status === ReleaseNoteStatus.DRAFT && (
            <>
              <Button
                type="button"
                variant="outlined"
                color="primary"
                label="Salvar rascunho"
                loading={pending === "draft"}
                disabled={Boolean(pending)}
                onClick={saveDraft}
              />
              <Button
                type="button"
                color="success"
                icon={<Send size={15} />}
                label="Publicar"
                loading={pending === "publish"}
                disabled={Boolean(pending)}
                onClick={publish}
              />
            </>
          )}
        </div>
      </footer>

      <Modal open={previewOpen} onOpenChange={setPreviewOpen} title={title.trim() || "Pré-visualização"} className="max-w-3xl">
        <ModalBody className="mt-4 max-h-[65vh] overflow-y-auto">
          <MarkdownPreview markdown={previewMarkdown} onMediaError={() => void refreshPreviewUrls()} />
        </ModalBody>
      </Modal>

      {canWrite && (
        <GenerateReleaseNoteModal
          open={generateOpen}
          hasContent={Boolean(title.trim() || currentMarkdown().trim())}
          onOpenChange={setGenerateOpen}
          onGenerated={applyGenerated}
        />
      )}

      <DeleteModal
        open={deleteOpen}
        title="Excluir nota"
        description="A nota some da lista e dos portais. O número da versão não é reaproveitado."
        confirmText="Excluir"
        loading={pending === "delete"}
        onClose={() => setDeleteOpen(false)}
        onDelete={() => void remove()}
      />
    </div>
  );
};

const SaveState = ({ dirty, hasId, locked }: { dirty: boolean; hasId: boolean; locked: boolean }) => {
  if (locked) return <span className="mr-2 text-sm text-ui-text-muted">Somente leitura</span>;

  const [dot, text] =
    dirty || !hasId ? ["bg-[#E5A000]", hasId ? "Alterações não salvas" : "Ainda não salvo"] : ["bg-[#99C32C]", "Tudo salvo"];
  return (
    <span className="mr-2 inline-flex items-center gap-2 text-sm text-ui-text-muted">
      <span className={cn("size-2 rounded-full", dot)} />
      {text}
    </span>
  );
};

export default ReleaseNoteEditor;
